/**
 * @file PickMarkForm.tsx
 * @description Поля панели «Отметить позицию»: что отмечаем и сколько
 * (phase19.24, решение пользователя 5 «как в приёмке»; кнопка уехала в
 * `footer` панели — этап 1, шаг 1).
 *
 * Отдельно от панели по той же причине, что `ReceiptItemForm` отдельно от
 * страницы приёмки: панель отвечает за раскладку и выбор строки, форма — за
 * одно действие «добавить N к собранному». Состояние живёт в
 * `usePickMarkForm`: кнопка «Отметить» стоит в другом слоте панели.
 */

import { WmsFieldLabel } from "../WmsFieldLabel";
import type { PickMarkFormState } from "./usePickMarkForm";
import {
  formatPickItemName,
  type PickingTaskItemDto,
} from "../../../lib/api-picking";
import { formatItemQty, pickUnit } from "../../../lib/picking-format";
import { qtyInputProps } from "../../../lib/qty-input";

interface Props {
  /** Выбранная строка задания; null — ещё не выбрана. */
  item: PickingTaskItemDto | null;
  state: PickMarkFormState;
  /** Ref поля: панель уводит сюда курсор после ручного выбора позиции. */
  qtyRef?: React.Ref<HTMLInputElement>;
}

export function PickMarkForm({ item, state, qtyRef }: Props) {
  // Пока строка не выбрана, единицы нет — реестр отдаёт целые штуки, и это
  // верный дефолт: панель открывается с «1» и ею же чаще всего отмечают.
  const qtyProps = qtyInputProps(item?.unit);

  return (
    <>
      {item ? (
        <p className="text-sm">
          <span className="font-medium">{formatPickItemName(item)}</span>
          <span className="block tabular-nums text-on-surface-variant">
            собрано {formatItemQty(item.qtyPicked, item.qtyPlanned, item.unit)}
          </span>
        </p>
      ) : null}

      {/* Единица в подписи поля: кладовщик вводит граммы, а не штуки, и
          должен видеть это до отправки, а не в собранном количестве. */}
      <WmsFieldLabel label={`Количество${item ? `, ${pickUnit(item.unit)}` : ""}`}>
        {/* Поле остаётся text: на складе в него летит ввод со сканера, а
            спиннер type=number ловит колесо мыши и портит количество.
            Клавиатуру и целочисленность берём из реестра единиц. */}
        <input
          ref={qtyRef}
          type="text"
          inputMode={qtyProps.inputMode}
          value={state.qty}
          onChange={(e) => state.setQty(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            state.mark();
          }}
          className="min-h-12 w-full rounded-lg bg-surface-lowest px-3 font-mono text-sm tabular-nums ring-ghost focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </WmsFieldLabel>
    </>
  );
}
